import React, { useContext } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { Modal, SpeakerTopicOverview, ProjectPreview, FinalistPreview } from 'components';
import { LocalContext } from 'context';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import BG1 from 'assets/svgs/bg-moon-1.svg';
import BG2 from 'assets/svgs/bg-moon-2.svg';
import BG3 from 'assets/svgs/broken-circle.svg';

const Card = ({ data, buttonText, cropped, popupType }) => {
  const { theme, mobile } = useContext(LocalContext);
  const [ref, inView] = useInView({
    rootMargin: '-96px 0px'
  });
  return (
    <>
      {data.isHost && (
        <StyledCard theme={theme} ref={ref} style={{ opacity: inView ? 1 : 0 }}>
          <CardImgContainer style={{ filter: inView && mobile ? 'none' : null }}>
            {popupType === 'speaker' && (
              <>
                <SVG
                  theme={theme}
                  style={{ opacity: inView && mobile ? 1 : 0, y: inView && mobile ? 0 : -50 }}>
                  <Circle1 />
                </SVG>
                <SVG
                  theme={theme}
                  style={{ opacity: inView && mobile ? 1 : 0, y: inView && mobile ? 0 : 50 }}>
                  <Circle2 />
                </SVG>
                <SVG
                  theme={theme}
                  style={{ opacity: inView && mobile ? 1 : 0, y: inView && mobile ? 0 : 50 }}>
                  <Circle3 />
                </SVG>
              </>
            )}
            <CardImg image={getImage(data.image.childImageSharp)} cropped={cropped} alt={data.name} />
          </CardImgContainer>
          <CardTitle>{data.name}</CardTitle>
          <CardTopics>
            {data.topics.map((topic, i) => (
              <CardTopic key={i}>{topic}</CardTopic>
            ))}
          </CardTopics>
        </StyledCard>
      )}
      {!data.isHost && (
        <Modal
          trigger={
            <StyledCard theme={theme} ref={ref} style={{ opacity: inView ? 1 : 0 }}>
              <CardImgContainer style={{ filter: inView && mobile ? 'none' : null }}>
                {popupType === 'speaker' && (
                  <>
                    <SVG
                      theme={theme}
                      style={{ opacity: inView && mobile ? 1 : 0, y: inView && mobile ? 0 : -50 }}>
                      <Circle1 />
                    </SVG>
                    <SVG
                      theme={theme}
                      style={{ opacity: inView && mobile ? 1 : 0, y: inView && mobile ? 0 : 50 }}>
                      <Circle2 />
                    </SVG>
                    <SVG
                      theme={theme}
                      style={{ opacity: inView && mobile ? 1 : 0, y: inView && mobile ? 0 : 50 }}>
                      <Circle3 />
                    </SVG>
                  </>
                )}
                <CardImg
                  image={getImage(data.image.childImageSharp)}
                  cropped={cropped}
                  alt={data.name}
                />
              </CardImgContainer>
              <CardTitle>{data.name}</CardTitle>
              <CardTopics>
                {data.topics.map((topic, i) => (
                  <CardTopic key={i}>{topic}</CardTopic>
                ))}
              </CardTopics>
              {!data.isHost && (
                <CardFooter>
                  <div style={{ alignSelf: 'center' }}>
                    <CardButton theme={theme} hover>
                      {buttonText}
                    </CardButton>
                  </div>
                </CardFooter>
              )}
            </StyledCard>
          }
          modalContent={
            popupType === 'speaker' ? (
              <SpeakerTopicOverview data={data} />
            ) : popupType === 'finalist' ? (
              <FinalistPreview startPoint={data.id} />
            ) : (
              <ProjectPreview startPoint={data.id} />
            )
          }
        />
      )}
    </>
  );
};

const CardImgContainer = styled(motion.div)`
  position: relative;
  overflow: visible;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 50px;
  padding-bottom: 30px;
  overflow: hidden;
  transition-duration: 1s;
  max-width: 300px;
  filter: none;
  left: 50%;
  transform: translateX(-50%);
`;

const SVG = styled(motion.div)`
  opacity: 1;
  width: auto;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  transition: 1s;
  svg {
    path {
      fill: ${({ theme }) => `${theme.fg} !important`};
      transition: 0.5s;
    }
  }
`;

const Circle1 = styled(BG1)`
  position: absolute;
  left: -3%;
  bottom: -3%;
  width: 85%;
  height: 85%;
  transition-duration: 1s;
  @media (min-width: 1150px) {
    bottom: -10%;
  }
`;
const Circle2 = styled(BG2)`
  position: absolute;
  right: -3%;
  top: -3%;
  width: 85%;
  height: 85%;
  transition-duration: 1s;

  @media (min-width: 1150px) {
    top: -10%;
  }
`;
const Circle3 = styled(BG3)`
  position: absolute;
  left: -5%;
  top: -8%;
  width: 85%;
  height: 85%;
  transition-duration: 1s;

  @media (min-width: 1150px) {
    top: -15%;
  }
`;

const CardImg = styled(GatsbyImage)`
  margin-bottom: 1.25rem;
  object-fit: cover;
  object-position: center;
  border-radius: ${({ cropped }) => (cropped ? '50%' : '0px')};
  transition: 0.5s;
  cursor: pointer;
  width: 100%;
  @media (min-width: 1150px) {
    filter: grayscale(100%);
  }
`;

const CardTitle = styled.h3`
  font-style: normal;
  font-weight: 700;
  font-size: 1.5rem;
  margin-top: 0px;
  margin-bottom: 0px;
  color: #333;
  text-align: center;
`;

const CardTopics = styled.p`
  text-align: center;
  font-size: 1.25rem;
  margin-bottom: 2rem;
  margin-top: 1rem;
`;

const CardTopic = styled.span`
  margin-bottom: 0;
  margin-right: 0.375rem;
  font-size: 1.25rem;
  line-height: 1.75rem;
  &:after {
    content: ',';
  }
  &:last-child {
    &:after {
      content: '';
    }
  }
`;

const CardButton = styled.div`
  height: 3rem;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Helvetica Neue';
  text-transform: uppercase;
  color: ${({ theme }) => theme.textColor};
  background-color: transparent;
  cursor: pointer;
  transition: 0.5s;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  :hover {
    color: ${({ theme }) => theme.fg};
  }
  @media (min-width: 1150px) {
    justify-content: center;
  }
`;

const CardFooter = styled.div`
  margin-top: auto;
  margin-bottom: 3rem;
`;

const StyledCard = styled(motion.div)`
  display: flex;
  height: 100%;
  flex-direction: column;
  width: 280px;
  max-width: 100%;
  transition-duration: 1s;
  margin: 0 1rem;
  &:hover ${CardImg} {
    box-shadow: 0px 0.375rem 0.75rem rgba(0, 0, 0, 0.25);
    filter: none;
  }
  &:hover ${CardButton} {
    color: ${({ theme }) => theme.fg};
  }

  @media (min-width: 1150px) {
    margin: 0 2rem;
    width: 300px;

    &:hover ${SVG} {
      opacity: 1 !important;
    }
    &:hover ${Circle1} {
      bottom: -15%;
    }
    &:hover ${Circle2} {
      top: -15%;
    }
    &:hover ${Circle3} {
      top: -20%;
    }
  }

  p,
  h3 {
    color: ${({ theme }) => theme.textColor};
  }
`;

export default Card;
